import type { Card, LiveListing, Product, Sale } from "@/context/AppContext";
import type { BundleStrategyId } from "@/features/bundleEngine";

export type SpeedBucket = "Fast" | "Medium" | "Slow";
export type SpeedBucketOrUnknown = SpeedBucket | "Unknown";

export interface SellThroughThresholds {
  fastMaxDays: number;
  slowMinDays: number;
}

export const DEFAULT_THRESHOLDS: SellThroughThresholds = {
  fastMaxDays: 7,
  slowMinDays: 21,
};

export const SLOW_INVENTORY_DEFAULT_DAYS = 30;

export interface ProductTiming {
  productId: string;
  product: Product;
  liveListing?: LiveListing;
  sale?: Sale;
  daysToList: number | null;
  daysToSell: number | null;
  daysSinceListed: number | null;
}

export interface DimensionAggregate {
  key: string;
  saleCount: number;
  avgDaysToSell: number;
  medianDaysToSell: number;
  avgDaysToList: number | null;
  speed: SpeedBucket;
}

export type ProductTypeAggregate = DimensionAggregate & { productType: string };

export interface StrategySpeedInfo {
  strategyId: BundleStrategyId;
  speed: SpeedBucketOrUnknown;
  sampleSize: number;
  avgDaysToSell: number | null;
}

export interface SlowInventoryItem {
  product: Product;
  liveListing: LiveListing;
  daysSinceListed: number;
}

function earliestBy<T>(
  items: T[],
  key: (item: T) => string | undefined,
  ts: (item: T) => string,
): Map<string, T> {
  const out = new Map<string, T>();
  for (const item of items) {
    const k = key(item);
    if (!k) continue;
    const existing = out.get(k);
    if (
      !existing ||
      new Date(ts(item)).getTime() < new Date(ts(existing)).getTime()
    ) {
      out.set(k, item);
    }
  }
  return out;
}

export function computeProductTimings(
  products: Product[],
  liveListings: LiveListing[],
  sales: Sale[],
  nowMs: number = Date.now(),
): ProductTiming[] {
  const listingByProduct = earliestBy(
    liveListings,
    (l) => l.productId,
    (l) => l.startedAt,
  );
  const saleByProduct = earliestBy(
    sales.filter((s) => s.itemType === "Product"),
    (s) => s.itemId,
    (s) => s.saleDate,
  );

  return products.map((product): ProductTiming => {
    const liveListing = listingByProduct.get(product.id);
    const sale = saleByProduct.get(product.id);
    const created = new Date(product.createdAt).getTime();

    // Prefer the listing.startedAt when present (active listing).
    // Fall back to sale.listedAt — captured at completeSale time so
    // sell-through math survives the listing being removed on sale.
    const startedSrc = liveListing?.startedAt ?? sale?.listedAt ?? null;
    const started = startedSrc ? new Date(startedSrc).getTime() : NaN;
    const sold = sale ? new Date(sale.saleDate).getTime() : NaN;

    const daysToList =
      Number.isFinite(created) && Number.isFinite(started) && started >= created
        ? (started - created) / 86400000
        : null;

    let daysToSell: number | null = null;
    if (sale && Number.isFinite(sold)) {
      if (Number.isFinite(started) && sold >= started) {
        daysToSell = (sold - started) / 86400000;
      } else if (Number.isFinite(created) && sold >= created) {
        daysToSell = (sold - created) / 86400000;
      }
    }

    const daysSinceListed =
      !sale && liveListing && Number.isFinite(started)
        ? Math.max(0, (nowMs - started) / 86400000)
        : null;

    return {
      productId: product.id,
      product,
      liveListing,
      sale,
      daysToList,
      daysToSell,
      daysSinceListed,
    };
  });
}

export function deriveThresholds(
  daysToSellSamples: number[],
): SellThroughThresholds {
  if (daysToSellSamples.length < 10) return DEFAULT_THRESHOLDS;
  const sorted = [...daysToSellSamples].sort((a, b) => a - b);
  const p33 = sorted[Math.floor(sorted.length * 0.33)];
  const p66 = sorted[Math.floor(sorted.length * 0.66)];
  return {
    fastMaxDays: Math.max(1, p33),
    slowMinDays: Math.max(p33 + 1, p66),
  };
}

export function bucketSpeed(
  days: number,
  thresholds: SellThroughThresholds = DEFAULT_THRESHOLDS,
): SpeedBucket {
  if (days <= thresholds.fastMaxDays) return "Fast";
  if (days > thresholds.slowMinDays) return "Slow";
  return "Medium";
}

function median(sortedAsc: number[]): number {
  if (sortedAsc.length === 0) return 0;
  const mid = sortedAsc.length / 2;
  if (sortedAsc.length % 2 === 0) {
    return (sortedAsc[mid - 1] + sortedAsc[mid]) / 2;
  }
  return sortedAsc[Math.floor(mid)];
}

export function aggregateByDimension(
  timings: ProductTiming[],
  keyFn: (t: ProductTiming) => string | string[] | null | undefined,
  thresholds: SellThroughThresholds = DEFAULT_THRESHOLDS,
  minSales = 2,
): DimensionAggregate[] {
  const sellGroups = new Map<string, number[]>();
  const listGroups = new Map<string, number[]>();
  for (const t of timings) {
    if (t.daysToSell == null) continue;
    const raw = keyFn(t);
    const keys = raw == null ? [] : Array.isArray(raw) ? raw : [raw];
    for (const key of keys) {
      if (!key) continue;
      const arr = sellGroups.get(key) ?? [];
      arr.push(t.daysToSell);
      sellGroups.set(key, arr);
      if (t.daysToList != null) {
        const la = listGroups.get(key) ?? [];
        la.push(t.daysToList);
        listGroups.set(key, la);
      }
    }
  }
  const out: DimensionAggregate[] = [];
  for (const [key, days] of sellGroups.entries()) {
    if (days.length < minSales) continue;
    const avg = days.reduce((a, b) => a + b, 0) / days.length;
    const sorted = [...days].sort((a, b) => a - b);
    const listDays = listGroups.get(key) ?? [];
    const avgList =
      listDays.length > 0
        ? listDays.reduce((a, b) => a + b, 0) / listDays.length
        : null;
    out.push({
      key,
      saleCount: days.length,
      avgDaysToSell: avg,
      medianDaysToSell: median(sorted),
      avgDaysToList: avgList,
      speed: bucketSpeed(avg, thresholds),
    });
  }
  return out.sort((a, b) => a.avgDaysToSell - b.avgDaysToSell);
}

export function aggregateByProductType(
  timings: ProductTiming[],
  thresholds: SellThroughThresholds = DEFAULT_THRESHOLDS,
  minSales = 2,
): ProductTypeAggregate[] {
  return aggregateByDimension(
    timings,
    (t) => t.product.productType,
    thresholds,
    minSales,
  ).map((a) => ({ ...a, productType: a.key }));
}

/** Aggregate by card set name. Walks each sold product's cardIds and credits every distinct set the product included. */
export function aggregateBySet(
  timings: ProductTiming[],
  cards: Card[],
  thresholds: SellThroughThresholds = DEFAULT_THRESHOLDS,
  minSales = 2,
): DimensionAggregate[] {
  const cardById = new Map(cards.map((c) => [c.id, c] as const));
  return aggregateByDimension(
    timings,
    (t) => {
      const sets = new Set<string>();
      for (const cid of t.product.cardIds ?? []) {
        const c = cardById.get(cid);
        if (c?.setName) sets.add(c.setName);
      }
      return Array.from(sets);
    },
    thresholds,
    minSales,
  );
}

/** Aggregate by card rarity. Same approach as aggregateBySet. */
export function aggregateByRarity(
  timings: ProductTiming[],
  cards: Card[],
  thresholds: SellThroughThresholds = DEFAULT_THRESHOLDS,
  minSales = 2,
): DimensionAggregate[] {
  const cardById = new Map(cards.map((c) => [c.id, c] as const));
  return aggregateByDimension(
    timings,
    (t) => {
      const rarities = new Set<string>();
      for (const cid of t.product.cardIds ?? []) {
        const c = cardById.get(cid);
        if (c?.rarity) rarities.add(c.rarity);
      }
      return Array.from(rarities);
    },
    thresholds,
    minSales,
  );
}

export function findSlowInventory(
  timings: ProductTiming[],
  thresholdDays: number = SLOW_INVENTORY_DEFAULT_DAYS,
  /** When provided, only include listings whose age is within this window (i.e. daysSinceListed <= periodDays). Use null for "no period cap". */
  periodDays: number | null = null,
): SlowInventoryItem[] {
  const out: SlowInventoryItem[] = [];
  for (const t of timings) {
    if (!t.liveListing || t.sale) continue;
    if (t.daysSinceListed == null) continue;
    if (t.daysSinceListed <= thresholdDays) continue;
    if (periodDays != null && t.daysSinceListed > periodDays) continue;
    out.push({
      product: t.product,
      liveListing: t.liveListing,
      daysSinceListed: t.daysSinceListed,
    });
  }
  return out.sort((a, b) => b.daysSinceListed - a.daysSinceListed);
}

/** Map key: `${strategyId}|${productType}` so different product types under the same strategy don't get conflated. */
export type StrategySpeedMap = Map<string, StrategySpeedInfo>;

function strategyKey(
  strategyId: BundleStrategyId,
  productType: string,
): string {
  return `${strategyId}|${productType}`;
}

export function computeStrategySellSpeed(
  products: Product[],
  liveListings: LiveListing[],
  sales: Sale[],
): StrategySpeedMap {
  const timings = computeProductTimings(products, liveListings, sales);
  const allSoldDays = timings
    .filter((t) => t.daysToSell != null)
    .map((t) => t.daysToSell!);
  const thresholds = deriveThresholds(allSoldDays);

  const groups = new Map<string, { sid: BundleStrategyId; days: number[] }>();
  for (const t of timings) {
    const sid = t.product.bundleStrategyId;
    const pt = t.product.productType;
    if (!sid || !pt || t.daysToSell == null) continue;
    const k = strategyKey(sid, pt);
    const entry = groups.get(k) ?? { sid, days: [] };
    entry.days.push(t.daysToSell);
    groups.set(k, entry);
  }
  const out: StrategySpeedMap = new Map();
  for (const [k, { sid, days }] of groups.entries()) {
    const avg = days.reduce((a, b) => a + b, 0) / days.length;
    out.set(k, {
      strategyId: sid,
      speed: bucketSpeed(avg, thresholds),
      sampleSize: days.length,
      avgDaysToSell: avg,
    });
  }
  return out;
}

export function getStrategySpeed(
  map: StrategySpeedMap,
  strategyId: BundleStrategyId,
  productType: string,
): StrategySpeedInfo {
  return (
    map.get(strategyKey(strategyId, productType)) ?? {
      strategyId,
      speed: "Unknown",
      sampleSize: 0,
      avgDaysToSell: null,
    }
  );
}

export function speedColor(
  speed: SpeedBucketOrUnknown,
  colors: { success?: string; secondary: string; mutedForeground: string },
): string {
  if (speed === "Fast") return colors.success ?? "#27ae60";
  if (speed === "Medium") return colors.secondary;
  if (speed === "Slow") return "#e74c3c";
  return colors.mutedForeground;
}
